import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { CSSProperties, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { formatCardTitleWithDeviation } from '../cardLengthDeviation';
import { cardMatchesSearch } from '../cardSearch';
import { getTeachingBlockCardGeometry } from '../getTeachingBlockCardGeometry';
import { useTeachingBlockCardResize } from '../hooks/useTeachingBlockCardResize';
import { TeachingBlockGridCardView } from '../TeachingBlockGridCardView/TeachingBlockGridCardView';
import { DOUBLE_CLICK_DELAY_MS, isCoexistableTeachingBlockCard, TeachingBlockCardType } from '../types';
import styles from './GridCard.module.scss';

type GridCardProps = {
  card: TeachingBlockCardType;
  left: string;
  width: string;
  height: string;
  top: string;
  background: string;
  textColor: string;
  onCardClick: (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => void;
};

export const GridCard = observer(
  ({ card, left, width, height, top, background, textColor, onCardClick }: GridCardProps) => {
    const store = useTeachingBlockStore();
    const { t } = useTranslation();

    // Perf: KEIN globales `store.pickedCard`-Abo. Die gepickte Karte wird von GridWeek
    // aus dem Grid gefiltert (placedCardsInWeek) und ist hier nie gerendert – das Abo
    // würde nur einen Full-Grid-Rerender beim Aufnehmen jeder Karte auslösen.
    const isPicked = !!card.virtualChildren && !!card.virtualChildren.find((uuid) => card.uuid.includes(uuid));

    const isPinned =
      card.uuid === store.pinnedCard?.uuid ||
      (!!card.virtualChildren && !!card.virtualChildren.find((uuid) => card.uuid.includes(uuid)));

    const isLocked = card.locked ?? false;

    const hasWarnings = store.getWarningsForCard(card.uuid).length > 0;
    const hasAvailabilityWarnings = !!card.availabilityWarnings && card.availabilityWarnings.length > 0;
    const showWarnings = store.highlightMode.includes('warnings') && (hasWarnings || hasAvailabilityWarnings);

    // Längen-Abweichung (resizte Epoche): bei aktivem Toggle die Differenz im Title
    // zeigen und – wie bei der Suche – alle NICHT abweichenden Karten ausgrauen, damit
    // die abweichenden hervorstechen.
    const lengthDeviationDays = store.getCardLengthDeviationDays(card);
    const lengthDeviationActive = store.highlightMode.includes('lengthDeviation');
    const showLengthDeviation = lengthDeviationActive && lengthDeviationDays !== 0;
    const dimmedByLengthDeviation = lengthDeviationActive && lengthDeviationDays === 0;

    // Kartensuche: nicht passende platzierte Karten ausgrauen (reaktiv via observer).
    const dimmedBySearch = store.cardSearchString.trim() !== '' && !cardMatchesSearch(card, store.cardSearchString);
    const dimmed = dimmedBySearch || dimmedByLengthDeviation;

    function onCardEditOpen() {
      store.setCardDurationModal({
        isOpen: true,
        card: card ?? undefined,
      });
    }

    const { menu, hasRooms } = useTeachingBlockCardMenu({
      card: card,
      styles,
      hasWarnings,
      hasAvailabilityWarnings,
      isPicked,
      isPinned,
      onCardEditOpen,
    });

    const range = {
      startDateString: dayjs(card.startDate).format('YYYY-MM-DD'),
      endDateString: dayjs(card.endDate).format('YYYY-MM-DD'),
    };
    const cardRef = useRef<HTMLDivElement>(null);

    const { activeEdge, canResizeEnd, canResizeStart, endHandleProps, startHandleProps } = useTeachingBlockCardResize({
      card,
      cardRef,
      getPreviewGeometry: (previewRange) =>
        getTeachingBlockCardGeometry(previewRange, {
          anchorDateString: range.startDateString,
          borderMode: 'grid',
        }),
      range,
    });

    const menuClasses = classNames(styles.menu, { [styles['has-warning']]: showWarnings });

    // Globale Marker-Klasse (wie `droppable` an den Tageszellen): lässt gestapelte
    // Wahlfach-Karten während eines Wahlfach-Picks die Maus durch, sonst fängt die
    // obenliegende Karte den Klick ab und wird aufgenommen statt darunter abgelegt.
    const cardClasses = classNames(styles['grid-card'], isCoexistableTeachingBlockCard(card) && 'coexistable-card', {
      [styles.picked]: isPicked,
      [styles.pinned]: isPinned,
      [styles.locked]: isLocked,
      [styles['is-resizing']]: !!activeEdge,
      [styles['resizing-start']]: activeEdge === 'start',
      [styles['resizing-end']]: activeEdge === 'end',
      [styles.warning]: showWarnings,
      [styles.room]: store.highlightMode.includes('rooms') && !hasRooms,
      // [styles['high-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   pinboardStore.controlMode.highlights.includes(card.uuid),
      // [styles['low-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   !pinboardStore.controlMode.highlights.includes(card.uuid),
    });

    const debounceOnCardHover = useDebounceCallback(() => {
      store.selectCard(toJS(card));
    }, 500);

    const onMouseEnter = useCallback(() => {
      if (store.resizingCard) return;
      if (!store.selectedCard) {
        store.selectCard(toJS(card));
      } else {
        debounceOnCardHover();
      }
    }, [card, debounceOnCardHover, store]);

    const onMouseLeave = useCallback(() => {
      if (store.resizingCard) return;
      debounceOnCardHover.cancel();
      // Perf: `store` (stabil) als Dependency statt `store.resizingCard` – der Callback
      // liest den Wert live; ein Render-Abo auf `resizingCard` würde sonst jede Karte
      // bei Resize-Start/-Ende neu rendern.
    }, [debounceOnCardHover, store]);

    const onOpenChange = useCallback(() => {
      debounceOnCardHover.cancel();
      store.selectCard(toJS(card));
    }, [card, debounceOnCardHover, store]);

    // Single- vs. Doppelklick entkoppeln: Der Einzelklick (Karte aufnehmen) wird
    // kurz verzögert, damit ein Doppelklick ihn abbrechen kann. Sonst würde der
    // erste Klick die Karte bereits aufnehmen und der Doppelklick nie greifen.
    const clickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
      () => () => {
        if (clickTimeout.current) clearTimeout(clickTimeout.current);
      },
      [],
    );

    const onCardClickDeferred = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (clickTimeout.current) clearTimeout(clickTimeout.current);
        clickTimeout.current = setTimeout(() => {
          clickTimeout.current = null;
          onCardClick(event, card);
        }, DOUBLE_CLICK_DELAY_MS);
      },
      [card, onCardClick],
    );

    const onCardDoubleClick = useCallback(() => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
        clickTimeout.current = null;
      }
      // Falls der verzögerte Einzelklick die Karte bereits aufgenommen hat, den Pick
      // sauber zurücknehmen (stellt die Verfügbarkeit wieder her), bevor Auto-Fit läuft.
      if (store.pickedCard?.card.uuid === card.uuid) store.onEscape(card);
      void store.autoFitCardToGap(card);
    }, [card, store]);

    const cardStyle = {
      '--card-left-base': left,
      '--card-width-base': width,
      background,
      color: textColor,
      filter: dimmed ? 'grayscale(1)' : undefined,
      height,
      opacity: dimmed ? 0.2 : undefined,
      top,
    } as CSSProperties;

    // Darstellung: LuL-Kürzel statt Fach-Kürzel auf dem Kärtchen (nichts anzeigen, wenn keine Lehrkraft).
    const teacherShortNames = card.teachers
      .map((teacher) => teacher.shortName)
      .filter(Boolean)
      .join(', ');
    const labelShort = store.showTeacherInsteadOfSubject ? teacherShortNames : card.labelShort;

    return (
      <TeachingBlockGridCardView
        activeEdge={activeEdge}
        canResizeEnd={canResizeEnd}
        canResizeStart={canResizeStart}
        cardClassName={cardClasses}
        cardRef={cardRef}
        cardStyle={cardStyle}
        endHandleProps={endHandleProps}
        hasRooms={store.highlightMode.includes('noIcons') ? true : hasRooms}
        isLocked={!!card.locked}
        isPinned={!!card.pinned}
        labelShort={labelShort}
        menu={menu}
        menuClassName={menuClasses}
        onCardClick={onCardClickDeferred}
        onCardDoubleClick={onCardDoubleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onOpenChange={onOpenChange}
        parent={document.getElementById('teaching-block-grid')}
        startHandleProps={startHandleProps}
        styles={styles}
        title={showLengthDeviation ? formatCardTitleWithDeviation(card.label, lengthDeviationDays, t) : card.label}
      />
    );
  },
);
